import { useEffect } from "react";
import {
  AddEmissionContext,
  EmissionMode,
  FormValuesStepTwo,
  getEvidenceFileInfo,
  EvidenceInfo,
} from "../../../../contexts/AddEmissionContext";
import { Activity, GetActivities } from "../../../../models/Activities";
import { DROPDOWN_EMISSION_GROUP } from "../../../../components/emission/emission-components";
import { Box, Button, Typography } from "@mui/material";
import CustomModal from "../../../../components/modal/CustomModal";
import { FormProvider } from "react-hook-form";
import { t } from "i18next";
import AddEmissionStepTwo from "../popup-add-emission/step2/AddEmissionStepTwo";
import alertService from "../../../../components/alert/alertService";
import dayjs, { Dayjs } from "dayjs";
import ActivitiesService from "../../../../service/activities.service";
import { DataEntryRoleContext } from "../../../../contexts/DataEntryRoleContext";

interface Props {
  data: Activity;
  open: boolean;
  onClose: () => void;
}

export const EditEmission = (props: Props) => {
  const {
    stepOneForm,
    stepTwoForm,
    subOrganizationList,
    prepareFormDataToCreateOrDraftActivities,
    setEmissionMode,
    refreshEmissionFactors,
  } = AddEmissionContext();

  const { updateActivitiesAfterEdit } = DataEntryRoleContext();

  const startDate: Dayjs = props.data.startDate as Dayjs;
  const endDate: Dayjs = props.data.endDate as Dayjs;

  const onSubmit = async (data: FormValuesStepTwo) => {
    const subOrgRefId =
      subOrganizationList.find((s) => s.name === props.data.suborganizationName)
        ?.refId ?? "";
    let evidence: EvidenceInfo;
    try {
      evidence = await getEvidenceFileInfo(
        stepTwoForm.getValues("attachmentRef"),
        stepTwoForm.getValues("attachment"),
        stepTwoForm.getValues("startDate").year(),
        subOrgRefId,
      );
    } catch (error) {
      // not every throw here is alerted: a corrupt attachment string fails in base64 decoding
      console.error("Evidence resolution failed before save", error);
      return;
    }
    let createActivitiesResult: GetActivities | undefined;
    try {
      createActivitiesResult = await ActivitiesService.update(
        props.data._id,
        prepareFormDataToCreateOrDraftActivities(evidence),
      );
    } catch {
      // error alert already shown by service
      return;
    }
    if (createActivitiesResult !== null) {
      close();
      await updateActivitiesAfterEdit();
      alertService.mixin(t("edit-emission-amount-successfully"));
    }
  };

  const close = () => {
    stepTwoForm.reset();
    stepOneForm.reset();
    props.onClose();
  };

  const setUpForm = () => {
    // form setup
    stepOneForm.setValue("assetName", props.data.assetRef);
    // stepTwoForm.setValue("startDate", dayjs(startDate));
    // stepTwoForm.setValue("endDate", dayjs(endDate));
    stepTwoForm.setValue("activityGroup", props.data.activityGroupEN);
    stepTwoForm.setValue("activityType", props.data.activityTypeEn);
    stepTwoForm.setValue("activityAttribute", props.data.activityAttributeEN);

    // ✅ V2: เซ็ตค่า Publisher ด้วย ID (emissionFactorRef) แทนชื่อ String
    // the stored factor only fits the stored period; a move during load keeps the new period's default
    const periodUnchanged =
      dayjs(stepTwoForm.getValues("startDate")).isSame(startDate, "day") &&
      dayjs(stepTwoForm.getValues("endDate")).isSame(endDate, "day");
    if (periodUnchanged) {
      stepTwoForm.setValue(
        "activityEmissionFactorPublisher",
        props.data.emissionFactorRef ?? "",
      );
    }

    stepTwoForm.setValue("amount", props.data.amount.toString() ?? "0");
    stepTwoForm.setValue("remark", props.data.remark ?? "");
    stepTwoForm.setValue("unit", props.data.unit ?? "");
    stepTwoForm.setValue("vendor", props.data.vendor ?? "");

    try {
      stepTwoForm.setValue("attachment", props.data.attachment ?? "");
      stepTwoForm.setValue("attachmentRef", props.data.attachmentRef ?? "");
    } catch (error) {
      // Blanking attachmentRef here would submit as a deliberate Remove; leave the field alone and surface the fault.
      console.error("Failed to load the activity's evidence into the form", error);
    }

    if (props.data.auxiliary) {
      stepTwoForm.setValue("aux1", props.data.auxiliary.aux1 ?? "");
      stepTwoForm.setValue("aux2", props.data.auxiliary.aux2 ?? "");
      stepTwoForm.setValue("aux3", props.data.auxiliary.aux3 ?? "");
      stepTwoForm.setValue("aux4", props.data.auxiliary.aux4 ?? "");
      stepTwoForm.setValue("aux5", props.data.auxiliary.aux5 ?? "");
    }

    stepTwoForm.setValue("monthly", props.data.generatedByTemplate ?? false);

    setEmissionMode(EmissionMode.AddEmission);
  };

  useEffect(() => {
    if (!props.open) return;

    // ✅ สร้างฟังก์ชัน async เพื่อจัดการลำดับการทำงานให้เป๊ะ
    const initData = async () => {
      const item = subOrganizationList.find(
        (i) => i.name === props.data.suborganizationName,
      );

      stepOneForm.setValue("subOrganizations", item?.id as string);
      stepOneForm.setValue(
        "location",
        `${item?.location?.address ?? ""} ${item?.location?.district ?? ""} ${
          item?.location?.city ?? ""
        }`.trim(),
      );

      const emissionGroupRef: string | undefined = DROPDOWN_EMISSION_GROUP.find(
        (i) => i.emissionGroup === props.data.emissionGroup,
      )?.emissionGroup;

      // 1️⃣ ยัด Group และ วันที่ ลงไปในฟอร์มก่อน! เพื่อให้ Context อ่านค่าไปยิง API ได้ถูก
      stepOneForm.setValue("emissionGroup", emissionGroupRef as string);
      stepTwoForm.setValue("startDate", dayjs(startDate));
      stepTwoForm.setValue("endDate", dayjs(endDate));

      // 2️⃣ สั่งยิง API (มันจะหยิบวันที่ที่เราเพิ่งยัดไปใช้)
      if (emissionGroupRef) {
        await refreshEmissionFactors();
      }

      // 3️⃣ โหลด API เสร็จ ค่อยรัน setUpForm เพื่อเขียนข้อมูลอื่นๆ (และ Publisher) ทับลงไป
      setUpForm();
    };

    initData();
  }, [props.open]);

  const OnError = () => {
    const inputError: string[] =
      Object.keys(stepTwoForm.formState.errors) ?? [];

    if (inputError.length > 0) {
      const form: HTMLElement | null = document.getElementById(inputError[0]);

      if (form)
        form.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
    }
  };

  return (
    <>
      <CustomModal
        header={<Typography variant="h1">{t("Edit-Emission")}</Typography>}
        body={
          <Box>
            <FormProvider {...stepTwoForm}>
              <form onSubmit={stepTwoForm.handleSubmit(onSubmit, OnError)}>
                <Box>
                  <AddEmissionStepTwo />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    marginTop: "44px",
                  }}
                  rowGap={2.5}
                >
                  <Button
                    fullWidth
                    color="secondary"
                    variant="contained"
                    type="submit"
                    name="add"
                    disabled={stepTwoForm.formState.isSubmitting}
                  >
                    {t("Save")}
                  </Button>
                </Box>
              </form>
            </FormProvider>
          </Box>
        }
        open={props.open}
        onClose={close}
      />
    </>
  );
};
